import { type FormEvent, useCallback, useEffect, useState } from "react";
import { authFetch, authFetchJson } from "@/lib/api";
import { normalizeSlug } from "@/lib/slug";
import { Pagination } from "@/components/Pagination";

type Department = {
  id: string;
  name: string;
  organization_id: string;
  organizations?: { name: string };
};

type Association = {
  id: string;
  department_id: string;
  name: string;
  slug: string;
  settings?: Record<string, unknown>;
  created_at?: string;
  departments?: { name: string; organization_id: string; organizations?: { name: string } };
};

function AssociationEditModal({
  assoc,
  onClose,
  onSaved,
}: {
  assoc: Association;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(assoc.name);
  const [slug, setSlug] = useState(assoc.slug);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      const trimmedName = name.trim();
      const normalizedSlug = normalizeSlug(slug);
      const payload: { name?: string; slug?: string } = {};
      if (trimmedName !== assoc.name) payload.name = trimmedName;
      if (normalizedSlug !== assoc.slug) payload.slug = normalizedSlug;
      if (Object.keys(payload).length === 0) {
        onClose();
        return;
      }
      await authFetchJson(`/api/associations/${assoc.id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Opslaan mislukt.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-lg rounded-2xl border border-[rgba(10,52,96,0.10)] bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-[var(--cs-navy)]">Associatie bewerken</h2>
          <button type="button" onClick={onClose} className="text-[var(--cs-ink-subtle)] hover:text-[var(--cs-navy)]">
            ✕
          </button>
        </div>

        <div className="mb-3 rounded-lg bg-[var(--cs-ivory)] p-3 text-xs text-[rgba(10,52,96,0.78)]">
          <p>ID: {assoc.id.slice(0, 8)}…</p>
          <p>
            Afdeling: {assoc.departments?.name ?? assoc.department_id.slice(0, 8) + "…"}
            {assoc.departments?.organizations?.name
              ? ` (${assoc.departments.organizations.name})`
              : ""}
          </p>
        </div>

        {error ? (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
            {error}
          </div>
        ) : null}

        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-[var(--cs-navy)]">Naam</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-[var(--cs-navy)]">Slug</label>
            <input
              type="text"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              className="w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 font-mono text-sm"
            />
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-[rgba(10,52,96,0.10)] px-4 py-1.5 text-sm text-[rgba(10,52,96,0.78)] hover:bg-[var(--cs-ivory)]"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={saving || !name.trim() || !normalizeSlug(slug)}
            className="rounded-lg bg-rose-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
          >
            {saving ? "Opslaan…" : "Opslaan"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdminAssociationsTab() {
  const [assocs, setAssocs] = useState<Association[]>([]);
  const [depts, setDepts] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newName, setNewName] = useState("");
  const [newDeptId, setNewDeptId] = useState("");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Association | null>(null);
  const [busyId, setBusyId] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    setPage(0);
  }, [pageSize]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [assocResp, deptResp] = await Promise.all([
        authFetchJson<{ associations: Association[] }>("/api/admin/associations"),
        authFetchJson<{ departments: Department[] }>("/api/admin/departments"),
      ]);
      setAssocs(assocResp.associations ?? []);
      const deptList = deptResp.departments ?? [];
      setDepts(deptList);
      if (deptList.length > 0 && !newDeptId) {
        setNewDeptId(deptList[0].id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Laden mislukt.");
    } finally {
      setLoading(false);
    }
  }, [newDeptId]);

  useEffect(() => {
    void load();
  }, [load]);

  const createAssoc = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      const name = newName.trim();
      if (!name || !newDeptId) return;
      setCreating(true);
      setError("");
      try {
        await authFetchJson(`/api/departments/${newDeptId}/associations`, {
          method: "POST",
          body: JSON.stringify({ name }),
        });
        setNewName("");
        await load();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Aanmaken mislukt.");
      } finally {
        setCreating(false);
      }
    },
    [newName, newDeptId, load],
  );

  const deleteAssoc = useCallback(
    async (assoc: Association) => {
      if (
        !window.confirm(
          `Associatie "${assoc.name}" permanent verwijderen? Dit kan niet ongedaan worden.`,
        )
      ) {
        return;
      }
      setBusyId(assoc.id);
      setError("");
      try {
        await authFetch(`/api/associations/${assoc.id}`, { method: "DELETE" });
        await load();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Verwijderen mislukt.");
      } finally {
        setBusyId("");
      }
    },
    [load],
  );

  const copyId = useCallback(async (id: string) => {
    await navigator.clipboard.writeText(id);
  }, []);

  const totalPages = Math.max(1, Math.ceil(assocs.length / pageSize));
  const currentPage = Math.min(page, totalPages - 1);
  const pagedAssocs = assocs.slice(
    currentPage * pageSize,
    (currentPage + 1) * pageSize,
  );

  return (
    <div className="space-y-4">
      <form
        onSubmit={createAssoc}
        className="flex flex-wrap items-end gap-2 rounded-2xl border border-[rgba(10,52,96,0.10)] bg-white p-4 shadow-sm"
      >
        <div className="min-w-0 flex-1">
          <label className="text-xs font-medium text-[var(--cs-navy)]" htmlFor="new-assoc-dept">
            Afdeling
          </label>
          <select
            id="new-assoc-dept"
            value={newDeptId}
            onChange={(e) => setNewDeptId(e.target.value)}
            className="mt-1 w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--cs-rose)]"
          >
            {depts.map((dept) => (
              <option key={dept.id} value={dept.id}>
                {dept.name}
                {dept.organizations ? ` (${dept.organizations.name})` : ""}
              </option>
            ))}
          </select>
        </div>
        <div className="min-w-0 flex-1">
          <label className="text-xs font-medium text-[var(--cs-navy)]" htmlFor="new-assoc-name">
            Naam
          </label>
          <input
            id="new-assoc-name"
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="bv. Hartfalen team"
            className="mt-1 w-full rounded-lg border border-[rgba(10,52,96,0.18)] px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--cs-rose)]"
          />
        </div>
        <button
          type="submit"
          disabled={creating || !newName.trim() || !newDeptId}
          className="rounded-xl bg-rose-500 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-600 disabled:opacity-50"
        >
          {creating ? "Aanmaken…" : "Aanmaken"}
        </button>
      </form>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      {loading ? (
        <p className="text-sm text-[var(--cs-ink-muted)]">Laden…</p>
      ) : assocs.length === 0 ? (
        <p className="text-sm text-[var(--cs-ink-muted)]">Nog geen associaties.</p>
      ) : (
        <>
        <div className="overflow-x-auto rounded-2xl border border-[rgba(10,52,96,0.10)] glass-card">
          <table className="w-full text-sm">
            <thead className="bg-[var(--cs-ivory)]">
              <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-[var(--cs-ink-muted)]">
                <th className="px-4 py-2">Naam</th>
                <th className="px-4 py-2">Slug</th>
                <th className="px-4 py-2">Afdeling</th>
                <th className="px-4 py-2">Organisatie</th>
                <th className="px-4 py-2">ID</th>
                <th className="px-4 py-2">Acties</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[rgba(10,52,96,0.06)]">
              {pagedAssocs.map((assoc) => (
                <tr key={assoc.id} className="text-xs">
                  <td className="px-4 py-2 font-medium text-[var(--cs-navy)]">{assoc.name}</td>
                  <td className="px-4 py-2 font-mono text-[rgba(10,52,96,0.78)]">{assoc.slug}</td>
                  <td className="px-4 py-2 text-[rgba(10,52,96,0.78)]">
                    {assoc.departments?.name ?? assoc.department_id.slice(0, 8) + "…"}
                  </td>
                  <td className="px-4 py-2 text-[rgba(10,52,96,0.78)]">
                    {assoc.departments?.organizations?.name ?? "—"}
                  </td>
                  <td className="px-4 py-2">
                    <button
                      type="button"
                      onClick={() => void copyId(assoc.id)}
                      className="font-mono text-[var(--cs-ink-muted)] hover:text-rose-600"
                      title="Kopieer ID"
                    >
                      {assoc.id.slice(0, 8)}…
                    </button>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => setEditing(assoc)}
                        disabled={busyId === assoc.id}
                        className="rounded border border-[rgba(10,52,96,0.10)] bg-white px-2 py-0.5 text-[10px] text-[var(--cs-navy)] hover:bg-[var(--cs-ivory)] disabled:opacity-50"
                      >
                        Bewerk
                      </button>
                      <button
                        type="button"
                        onClick={() => void deleteAssoc(assoc)}
                        disabled={busyId === assoc.id}
                        className="rounded border border-red-200 bg-white px-2 py-0.5 text-[10px] text-red-700 hover:bg-red-50 disabled:opacity-50"
                      >
                        Verwijder
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination
          total={assocs.length}
          page={currentPage}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
        </>
      )}

      {editing ? (
        <AssociationEditModal
          assoc={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      ) : null}
    </div>
  );
}
